import { authenticatedFetch } from "@beholos/holoscommon";
import { API_BASE_URL } from "./constants";

export const sendReferral = async (emails: string): Promise<string> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/send-referral`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        emails
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data.message || '¡Invitaciones enviadas exitosamente!';
  } catch (error: any) {
    console.error('Error sending the referral:', error);
    throw new Error('Se ha producido un error.');
  }
};

export interface UserNotification {
  id: string;
  user?: string;
  read: boolean;
  dismissed: boolean;
  allUsers?: boolean;
  date: string;
  type?: string;
  title: string;
  message: string;
  link?: string;
  linkText?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  limit: number;
  page: number;
  totalDocs: number;
  totalPages: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  pagingCounter: number;
  prevPage: number | null;
  nextPage: number | null;
}

export const getUserNotifications = async (
  page: number = 1
): Promise<PaginatedResponse<UserNotification>> => {
  const url = `${API_BASE_URL}/users/notifications?page=${page}`;
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = (await response.json()) as PaginatedResponse<any>;
    // Normalize id field to `id` to simplify consumers
    const normalizedDocs: UserNotification[] = raw.docs.map((n: any) => ({
      id: n.id || n._id,
      user: n.user,
      read: Boolean(n.read),
      dismissed: Boolean(n.dismissed),
      allUsers: n.allUsers,
      date: typeof n.date === 'string' ? n.date : new Date(n.date).toISOString(),
      type: n.type,
      title: n.title,
      message: n.message,
      link: n.link,
      linkText: n.linkText,
    }));
    return { ...raw, docs: normalizedDocs } as PaginatedResponse<UserNotification>;
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    // Return an empty paginate shape to simplify consumers
    return {
      docs: [],
      limit: 20,
      page,
      totalDocs: 0,
      totalPages: 0,
      hasPrevPage: false,
      hasNextPage: false,
      pagingCounter: 0,
      prevPage: null,
      nextPage: null,
    };
  }
};

export const updateUserNotification = async (
  id: string,
  body: { read?: boolean; dismissed?: boolean }
): Promise<UserNotification | null> => {
  const url = `${API_BASE_URL}/users/notifications/${id}`;
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as UserNotification;
    return data;
  } catch (error) {
    console.error('Error updating user notification:', error);
    return null;
  }
};

export interface DashboardDesignValues {
  [key: string]: unknown;
  primaryColor?: string;
  fullWidthDashboardBanner?: boolean;
  dashboardBannerImage?: string;
  dashboardBannerTextColor?: string;
  studioImage?: string;
}

export const getDashboardDesignValues = async (): Promise<DashboardDesignValues | null> => {
  const url = `${API_BASE_URL}/users/dashboard-design-values`;
  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as DashboardDesignValues;
    return data;
  } catch (error) {
    console.error('Error fetching dashboard design values:', error);
    return null;
  }
};

// Set user timezone
export const setUserTimeZone = async (timeZone: string): Promise<boolean> => {
  const url = `${API_BASE_URL}/users/set-timezone`;
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ timeZone })
    });

    if (!response.ok) {
      // eslint-disable-next-line no-console
      try {
        const text = await response.text();
        console.error('Failed to set timezone', response.status, text);
      } catch {
        console.error('Failed to set timezone', response.status);
      }
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error setting user timezone:', error);
    return false;
  }
};

export const getUserClassByHash = async (hash: string): Promise<any> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/user-class/details/${encodeURIComponent(hash)}`, {
      method: "GET",
      headers: { "Accept": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch details: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('data', data);
    return data;
  } catch (error) {
    console.error("Error fetching user class by hash:", error);
    throw error;
  }
}

export const verifyUserClassByHash = async (hash: string): Promise<boolean> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/user-class/verify/${encodeURIComponent(hash)}`, {
    method: "PUT",
  });
  return response.ok;
}

export const deleteUser = async (password: string): Promise<boolean> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/users/delete`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
  return response.ok;
}

export const getYearInReviewData = async (year: number): Promise<any> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/year-in-review/${year}`);

    if (!response.ok) throw new Error("Request failed");

    const data = await response.json();

    return data;

  } catch (error) {
    console.error("Error fetching year in review data:", error);

    return null;
  }
}